import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import BaseCard from '@repo/ui/components/cards/BaseCard';
import Button from '@repo/ui/components/Button';
import { Spinner } from '@repo/ui/components/Spinner';
import { useSession } from '../hooks/auth/useSession';
import { useProfile } from '../hooks/auth/useProfile';
import { useLogout } from '../hooks/auth/useLogout';
import { useIsMobile } from '../hooks/useIsMobile';
import { User, Shield, Clock, Link2, LogOut, ChevronRight, LucideIcon } from 'lucide-react';
import { formatDate } from '../utils/dateFormatters';
import { useLinkStatus } from '../hooks/auth/usePasswordSync';
import PasswordSyncModal from './password-sync/PasswordSyncModal';
import { PASSWORD_SYNC_COPY } from './password-sync/constants';
import { LightModal } from '@repo/ui/components/Modal/LightModal';

function PasswordSyncSection({
  isLinked,
  isEligible,
  onSetUp,
}: {
  isLinked: boolean;
  isEligible: boolean;
  onSetUp: () => void;
}) {
  if (isLinked) {
    return null;
  }

  if (!isEligible) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3.5 ring-1 ring-gray-200">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
        <Link2 className="h-4 w-4 text-blue-600" aria-hidden="true" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-800">
          {PASSWORD_SYNC_COPY.profileSection.unlinkedHeading}
        </p>
        <p className="text-xs text-gray-500">
          {PASSWORD_SYNC_COPY.profileSection.unlinkedSubtitle}
        </p>
      </div>
      <Button variant="outline" onClick={onSetUp}>
        {PASSWORD_SYNC_COPY.profileSection.activateButton}
      </Button>
    </div>
  );
}

interface ProfileAttributeProps {
  icon: LucideIcon;
  title: string;
  value: string;
  iconColor?: string;
  isMobile: boolean;
}

function ProfileAttribute({
  icon: Icon,
  title,
  value,
  iconColor = 'text-gray-600',
  isMobile,
}: ProfileAttributeProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
      <Icon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p
          className={`font-semibold [overflow-wrap:anywhere] ${isMobile ? 'text-base' : 'text-lg'}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

export default function ProfileCard() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { session } = useSession();
  const { data: userProfile, isLoading: profileLoading, error: profileError } = useProfile();
  const { data: linkStatus, isLoading: statusLoading, error: statusError } = useLinkStatus();
  const [modalOpen, setModalOpen] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const isLinked = !!(linkStatus?.isLinked || syncSuccess);
  const { mutate: logout, isPending: isLoggingOut } = useLogout({
    onSuccess: () => {
      setLogoutConfirmOpen(false);
      navigate({ to: '/login', replace: true });
    },
  });

  if (!session) {
    return null;
  }

  const getFullName = () => {
    if (!userProfile) return 'N/A';

    const parts = [
      userProfile.formofaddress,
      userProfile.fname,
      userProfile.mname,
      userProfile.lname,
      userProfile.suffix,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' ') : 'N/A';
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <BaseCard title="Profile" subtitle="Your account information" className="w-full">
        {profileLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {profileError && (
          <div className="py-8 text-center text-red-600">Failed to load profile information</div>
        )}

        {!profileLoading && !profileError && (
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div className="space-y-4">
              {userProfile && (
                <ProfileAttribute
                  icon={User}
                  title="Full Name"
                  value={getFullName()}
                  iconColor="text-blue-600"
                  isMobile={isMobile}
                />
              )}

              {session.approle && (
                <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
                  <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-purple-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-500">Role</p>
                    <p
                      className={`font-semibold [overflow-wrap:anywhere] ${isMobile ? 'text-base' : 'text-lg'}`}
                    >
                      {session.approle}
                    </p>
                    {(session.iselevated || session.isglobal) && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {session.iselevated && (
                          <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            Elevated
                          </span>
                        )}
                        {session.isglobal && (
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            Global
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {userProfile && (
                <>
                  <ProfileAttribute
                    icon={Clock}
                    title="Profile Created"
                    value={formatDate(userProfile.name_created)}
                    iconColor="text-blue-600"
                    isMobile={isMobile}
                  />
                </>
              )}
              <ProfileAttribute
                icon={Clock}
                title="Login Time"
                value={formatDate(session.sidlogonts)}
                iconColor="text-orange-600"
                isMobile={isMobile}
              />

              <ProfileAttribute
                icon={Clock}
                title="Last Activity"
                value={formatDate(session.sidlastts)}
                iconColor="text-green-600"
                isMobile={isMobile}
              />
            </div>
          </div>
        )}

        {!profileLoading &&
          !profileError &&
          !isLinked &&
          (linkStatus?.isEligible || statusLoading || statusError) && (
            <div className="mt-6 border-t border-gray-100 pt-5">
              {statusLoading ? (
                <div className="h-[60px] animate-pulse rounded-xl bg-gray-100" role="status">
                  <span className="sr-only">Loading sync status…</span>
                </div>
              ) : statusError ? (
                <p className="text-sm text-gray-500">Unable to load sync status.</p>
              ) : (
                <PasswordSyncSection
                  isLinked={isLinked}
                  isEligible={linkStatus?.isEligible ?? true}
                  onSetUp={() => setModalOpen(true)}
                />
              )}
            </div>
          )}

        <PasswordSyncModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={() => setSyncSuccess(true)}
        />

        {isMobile && (
          <div className="mt-6 border-t border-gray-100 pt-5">
            <button
              onClick={() => setLogoutConfirmOpen(true)}
              className="flex w-full items-center gap-3 rounded-lg p-4 text-left transition-colors hover:bg-gray-50"
            >
              <LogOut className="h-5 w-5 flex-shrink-0 text-gray-600" />
              <span className="flex-1 font-semibold text-gray-800">Log Out</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
            </button>
          </div>
        )}
      </BaseCard>

      <LightModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title="Log Out"
        className="max-w-md"
      >
        <p className="mb-6 text-center text-gray-600">Are you sure you want to log out?</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => setLogoutConfirmOpen(false)}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
            disabled={isLoggingOut}
          >
            Cancel
          </button>
          <button
            onClick={() => logout()}
            className="flex items-center gap-1 rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            disabled={isLoggingOut}
          >
            {isLoggingOut ? (
              <>
                <span className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4" />
                Log Out
              </>
            )}
          </button>
        </div>
      </LightModal>
    </div>
  );
}
